import { supabase } from './supabase';
import { TrainerMemberService } from './trainerMemberService';
import { ChatMessage, User } from '../types/dataModel';
import { encodeMessage, decodeMessage } from '../utils/messageEncoding';
import { displayNameFromProfile } from '../pages/account/accountShared';

function userRowToUser(row: {
  id: string;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  role?: string | null;
  created_at?: string;
  updated_at?: string;
}): User {
  return {
    id: row.id,
    email: row.email || '',
    first_name: typeof row.first_name === 'string' ? row.first_name : '',
    last_name: typeof row.last_name === 'string' ? row.last_name : '',
    role: row.role === 'trainer' ? 'trainer' : 'member',
    createdAt: row.created_at || new Date().toISOString(),
    updatedAt: row.updated_at || new Date().toISOString(),
  };
}

export interface ChatConversation {
  otherUser: User;
  otherUserId: string;
  lastMessage?: ChatMessage;
  unreadCount?: number;
  /** When current user is trainer: 'pending' = connection not yet accepted */
  relationshipStatus?: 'active' | 'pending';
  /** Set for pending invitations (member view) so Accept/Reject can be called */
  relationshipId?: string;
}

export class ChatService {
  /**
   * Get list of people the current user can chat with
   * - Trainers: their active and pending members (so they can message pending members)
   * - Members: their active trainers
   */
  static async getConversations(userId: string, role: 'trainer' | 'member'): Promise<ChatConversation[]> {
    try {
      console.log('💬 getConversations called:', { userId, role });
      
      // Verify user ID by checking profile
      const { data: userProfile } = await supabase
        .from('users')
        .select('id, email, first_name, last_name, role')
        .eq('id', userId)
        .maybeSingle();
      
      console.log('👤 User profile lookup:', { 
        userId, 
        found: !!userProfile,
        email: userProfile?.email,
        displayName: displayNameFromProfile(userProfile ?? undefined),
        role: userProfile?.role
      });
      
      if (role === 'trainer') {
        const relationships = await TrainerMemberService.getTrainerMembers(userId);
        console.log('📋 Trainer relationships:', relationships.length);
        const chatableRelationships = relationships.filter(
          r => (r.status === 'active' || r.status === 'pending') && r.member
        );
        console.log('✅ Chatable relationships (active + pending):', chatableRelationships.length);
        
        // Fetch latest messages for each conversation
        const conversations: ChatConversation[] = [];
        for (const r of chatableRelationships) {
          let otherUser = r.member;
          if (!otherUser && r.memberId) {
            const fetched = await ChatService.getOtherUser(r.memberId);
            if (fetched) otherUser = fetched;
          }
          if (otherUser) {
            const latestMessage = await this.getLatestMessage(userId, r.memberId);
            conversations.push({
              otherUser,
              otherUserId: r.memberId,
              lastMessage: latestMessage,
              relationshipStatus: (r.status === 'pending' ? 'pending' : 'active') as 'active' | 'pending',
            });
          }
        }
        conversations.sort((a, b) => {
          const at = a.lastMessage?.createdAt ?? '';
          const bt = b.lastMessage?.createdAt ?? '';
          return bt.localeCompare(at);
        });
        return conversations;
      } else {
        /* Member: only active relationships. Pending invitations are handled on the Notifications page. */
        console.log('🔍 Querying relationships for member:', userId);
        const { data: relationships, error: relError } = await supabase
          .from('trainer_member_relationships')
          .select(`
            *,
            trainer:users!trainer_id (id, email, first_name, last_name, role, created_at, updated_at)
          `)
          .eq('member_id', userId)
          .eq('status', 'active');

        if (relError) {
          console.error('❌ Error fetching relationships:', relError);
          throw relError;
        }

        console.log('📋 Found relationships:', relationships?.length || 0, relationships);

        const results: ChatConversation[] = [];
        for (const r of relationships || []) {
          let trainer = Array.isArray((r as any).trainer) ? (r as any).trainer[0] : (r as any).trainer;
          const trainerId = (r as any).trainer_id;
          const status = (r as any).status as string;
          
          console.log('👤 Processing trainer:', { trainerId, trainer });
          
          // If trainer data is missing from join (RLS might be blocking), fetch it separately
          if (!trainer && trainerId) {
            console.log('⚠️ Trainer data missing from join, fetching separately for trainer_id:', trainerId);
            try {
              const { data: trainerInfo, error: trainerError } = await supabase
                .from('users')
                .select('id, email, first_name, last_name, role, created_at, updated_at')
                .eq('id', trainerId)
                .maybeSingle();
              
              console.log('🔍 Separate trainer fetch result:', { 
                hasData: !!trainerInfo, 
                error: trainerError,
                trainerInfo 
              });
              
              if (!trainerError && trainerInfo) {
                trainer = trainerInfo;
                console.log('✅ Fetched trainer data separately:', trainer);
              } else if (trainerError) {
                console.error('❌ Error fetching trainer data:', trainerError);
                console.error('❌ Error details:', {
                  code: trainerError.code,
                  message: trainerError.message,
                  details: trainerError.details,
                  hint: trainerError.hint
                });
              } else {
                console.warn('⚠️ No trainer data and no error - RLS might be blocking or user not found');
              }
            } catch (err) {
              console.error('❌ Exception fetching trainer data:', err);
            }
          }
          
          if (trainer) {
            const latestMessage = await this.getLatestMessage(userId, trainerId);
            results.push({
              otherUser: userRowToUser(trainer),
              otherUserId: trainerId,
              lastMessage: latestMessage,
              relationshipStatus: 'active' as const,
            });
          } else {
            console.warn('⚠️ Skipping relationship - trainer data not available:', trainerId);
          }
        }
        results.sort((a, b) => {
          const at = a.lastMessage?.createdAt ?? '';
          const bt = b.lastMessage?.createdAt ?? '';
          return bt.localeCompare(at);
        });
        console.log('✅ Returning conversations:', results.length);
        return results;
      }
    } catch (error) {
      console.error('❌ Error fetching conversations:', error);
      throw error;
    }
  }

  /**
   * Get the latest message between current user and another user
   */
  static async getLatestMessage(userId: string, otherUserId: string): Promise<ChatMessage | undefined> {
    try {
      const { data, error } = await supabase
        .from('chat_message')
        .select('*')
        .or(`and(sender_id.eq.${userId},receiver_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},receiver_id.eq.${userId})`)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('❌ Error fetching latest message:', error);
        return undefined;
      }

      if (!data) return undefined;

      return {
        id: data.id,
        senderId: data.sender_id,
        receiverId: data.receiver_id,
        content: decodeMessage(data.content),
        createdAt: data.created_at,
        readAt: data.read_at,
        type: (data.message_type === 'system' ? 'system' : 'user') as 'user' | 'system',
      };
    } catch (error) {
      console.error('❌ Error fetching latest message:', error);
      return undefined;
    }
  }

  /**
   * Fetch a user by id (for building conversation list when join fails or for openUserId fallback)
   */
  static async getOtherUser(userId: string): Promise<User | null> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, email, first_name, last_name, role, created_at, updated_at')
        .eq('id', userId)
        .maybeSingle();
      if (error || !data) return null;
      return userRowToUser(data);
    } catch {
      return null;
    }
  }

  /**
   * Get messages between current user and another user
   */
  static async getMessages(userId: string, otherUserId: string, limit = 100): Promise<ChatMessage[]> {
    try {
      console.log('🔍 Fetching messages:', { userId, otherUserId });
      
      const { data, error } = await supabase
        .from('chat_message')
        .select('*')
        .or(`and(sender_id.eq.${userId},receiver_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},receiver_id.eq.${userId})`)
        .order('created_at', { ascending: true })
        .limit(limit);

      if (error) {
        console.error('❌ Error fetching messages:', error);
        throw error;
      }

      console.log('✅ Messages fetched:', data?.length || 0, 'messages');
      if (data && data.length > 0) {
        console.log('📨 Sample message:', data[0]);
      }

      return (data || []).map((m: any) => ({
        id: m.id,
        senderId: m.sender_id,
        receiverId: m.receiver_id,
        content: decodeMessage(m.content),
        createdAt: m.created_at,
        readAt: m.read_at,
        type: (m.message_type === 'system' ? 'system' : 'user') as 'user' | 'system',
      }));
    } catch (error) {
      console.error('❌ Error fetching messages:', error);
      throw error;
    }
  }

  /**
   * Insert a system message (e.g. "You're connected! Say hello to get started.").
   * Call after member accepts an invitation; stored in DB so it appears in order and persists.
   * If message_type column is missing (migration not run), inserts as normal message so it still appears.
   */
  static async insertSystemMessage(senderId: string, receiverId: string, content: string): Promise<ChatMessage> {
    const payload: Record<string, unknown> = {
      sender_id: senderId,
      receiver_id: receiverId,
      content: encodeMessage(content),
    };
    try {
      const { data, error } = await supabase
        .from('chat_message')
        .insert({ ...payload, message_type: 'system' })
        .select()
        .single();

      if (error) {
        if (error.code === '42703' || error.message?.includes('message_type')) {
          const { data: fallbackData, error: fallbackError } = await supabase
            .from('chat_message')
            .insert(payload)
            .select()
            .single();
          if (fallbackError) throw fallbackError;
          return {
            id: fallbackData.id,
            senderId: fallbackData.sender_id,
            receiverId: fallbackData.receiver_id,
            content: decodeMessage(fallbackData.content),
            createdAt: fallbackData.created_at,
            readAt: fallbackData.read_at,
            type: 'system',
          };
        }
        throw error;
      }

      return {
        id: data.id,
        senderId: data.sender_id,
        receiverId: data.receiver_id,
        content: decodeMessage(data.content),
        createdAt: data.created_at,
        readAt: data.read_at,
        type: 'system',
      };
    } catch (error) {
      console.error('Error inserting system message:', error);
      throw error;
    }
  }

  /**
   * Total count of user messages (not system) received by userId that are not yet read.
   * Used for notification badge on chat icon. System messages (e.g. "You're connected!") are excluded.
   */
  static async getUnreadCount(userId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('chat_message')
        .select('*', { count: 'exact', head: true })
        .eq('receiver_id', userId)
        .is('read_at', null)
        .or('message_type.eq.user,message_type.is.null');

      if (error) {
        console.warn('getUnreadCount error:', error);
        return 0;
      }
      return typeof count === 'number' ? count : 0;
    } catch (err) {
      console.warn('getUnreadCount error:', err);
      return 0;
    }
  }

  /**
   * Mark all messages from otherUserId to current user as read.
   * Call when the user opens/views the conversation.
   */
  static async markAsRead(userId: string, otherUserId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('chat_message')
        .update({ read_at: new Date().toISOString() })
        .eq('receiver_id', userId)
        .eq('sender_id', otherUserId)
        .is('read_at', null);

      if (error) {
        console.warn('Could not mark messages as read:', error);
      }
    } catch (err) {
      console.warn('markAsRead error:', err);
    }
  }

  /**
   * Send a message
   */
  static async sendMessage(senderId: string, receiverId: string, content: string): Promise<ChatMessage> {
    try {
      const trimmed = content.trim();
      if (!trimmed) throw new Error('Message cannot be empty');

      const { data, error } = await supabase
        .from('chat_message')
        .insert({
          sender_id: senderId,
          receiver_id: receiverId,
          content: encodeMessage(trimmed),
        })
        .select()
        .single();

      if (error) throw error;

      return {
        id: data.id,
        senderId: data.sender_id,
        receiverId: data.receiver_id,
        content: decodeMessage(data.content),
        createdAt: data.created_at,
        readAt: data.read_at,
      };
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  /**
   * Subscribe to all new messages where current user is receiver.
   * Use this to update the conversation list in real time (e.g. when viewing the list or when a message arrives in another thread).
   */
  static subscribeToAllIncomingMessages(
    userId: string,
    onNewMessage: (message: ChatMessage) => void
  ): { unsubscribe: () => void } {
    const channel = supabase
      .channel(`chat-all:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_message',
          filter: `receiver_id=eq.${userId}`,
        },
        (payload) => {
          const m = payload.new as any;
          onNewMessage({
            id: m.id,
            senderId: m.sender_id,
            receiverId: m.receiver_id,
            content: decodeMessage(m.content),
            createdAt: m.created_at,
            readAt: m.read_at,
            type: (m.message_type === 'system' ? 'system' : 'user') as 'user' | 'system',
          });
        }
      )
      .subscribe();
    return { unsubscribe: () => ChatService.unsubscribe(channel) };
  }

  /**
   * Subscribe to new messages in a conversation (for real-time updates)
   * Subscribes to messages where current user is receiver (incoming messages)
   */
  static subscribeToMessages(
    userId: string,
    otherUserId: string,
    onNewMessage: (message: ChatMessage) => void
  ) {
    const channel = supabase
      .channel(`chat:${userId}:${otherUserId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_message',
          filter: `receiver_id=eq.${userId}`,
        },
        (payload) => {
          const m = payload.new as any;
          if (m.sender_id === otherUserId) {
            onNewMessage({
              id: m.id,
              senderId: m.sender_id,
              receiverId: m.receiver_id,
              content: decodeMessage(m.content),
              createdAt: m.created_at,
              readAt: m.read_at,
              type: (m.message_type === 'system' ? 'system' : 'user') as 'user' | 'system',
            });
          }
        }
      )
      .subscribe();
    return channel;
  }

  /**
   * Unsubscribe from channel
   */
  static unsubscribe(channel: { unsubscribe: () => void }) {
    if (channel?.unsubscribe) channel.unsubscribe();
  }
}
